import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  BookOpen, 
  MessageSquare, 
  Heart, 
  TrendingUp,
  Calendar,
  Clock,
  Eye
} from 'lucide-react';

const Dashboard = ({ stats, onRefresh }) => {
  if (!stats) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner"></div>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color, change, changeType }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="glass-card p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        {change && (
          <div className={`flex items-center space-x-1 text-sm ${
            changeType === 'positive' ? 'text-green-400' : 'text-red-400'
          }`}>
            <TrendingUp className="w-4 h-4" />
            <span>{change}</span>
          </div>
        )}
      </div>
      <h3 className="text-2xl font-bold text-text-primary mb-1">{value}</h3>
      <p className="text-text-secondary">{title}</p>
    </motion.div>
  );

  const RecentItem = ({ item, type }) => {
    if (type === 'monument') {
      return (
        <div className="flex items-center space-x-3 p-3 hover:bg-glass-bg/30 rounded-lg transition-colors">
          <div className="w-10 h-10 bg-accent-primary/20 rounded-lg flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-accent-primary" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-text-primary">{item.title}</p>
            <p className="text-sm text-text-secondary flex items-center space-x-2">
              <Calendar className="w-3 h-3" />
              <span>{new Date(item.createdAt).toLocaleDateString()}</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-text-secondary">{item.likes} likes</p>
          </div>
        </div>
      );
    }

    if (type === 'user') {
      return (
        <div className="flex items-center space-x-3 p-3 hover:bg-glass-bg/30 rounded-lg transition-colors">
          <div className="w-10 h-10 bg-accent-primary/20 rounded-full flex items-center justify-center">
            <Users className="w-5 h-5 text-accent-primary" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-text-primary">{item.username}</p>
            <p className="text-sm text-text-secondary flex items-center space-x-2">
              <Calendar className="w-3 h-3" />
              <span>{new Date(item.createdAt).toLocaleDateString()}</span>
            </p>
          </div>
          <div className="text-right">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              item.role === 'admin' 
                ? 'bg-accent-primary/20 text-accent-primary' 
                : 'bg-text-muted/20 text-text-secondary'
            }`}>
              {item.role}
            </span>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-serif font-bold text-text-primary">
          Dashboard Overview
        </h2>
        <button
          onClick={onRefresh}
          className="btn-secondary flex items-center space-x-2"
        >
          <Clock className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Monuments"
          value={stats.stats.totalMonuments}
          icon={BookOpen}
          color="bg-blue-500"
        />
        <StatCard
          title="Total Users"
          value={stats.stats.totalUsers}
          icon={Users}
          color="bg-green-500"
        />
        <StatCard
          title="Total Comments"
          value={stats.stats.totalComments}
          icon={MessageSquare}
          color="bg-purple-500"
        />
        <StatCard
          title="Total Likes"
          value={stats.stats.totalLikes}
          icon={Heart}
          color="bg-red-500"
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Monuments */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="glass-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-text-primary">
              Recent Monuments
            </h3>
            <Eye className="w-5 h-5 text-text-muted" />
          </div>
          <div className="space-y-2">
            {stats.recentMonuments.length > 0 ? (
              stats.recentMonuments.map((monument, index) => (
                <RecentItem
                  key={monument._id}
                  item={monument}
                  type="monument"
                />
              ))
            ) : (
              <div className="text-center py-8">
                <BookOpen className="w-12 h-12 text-text-muted mx-auto mb-3" />
                <p className="text-text-secondary">No monuments yet</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Recent Users */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="glass-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-text-primary">
              Recent Users
            </h3>
            <Users className="w-5 h-5 text-text-muted" />
          </div>
          <div className="space-y-2">
            {stats.recentUsers.length > 0 ? (
              stats.recentUsers.map((user, index) => (
                <RecentItem
                  key={user._id}
                  item={user}
                  type="user"
                />
              ))
            ) : (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-text-muted mx-auto mb-3" />
                <p className="text-text-secondary">No users yet</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="mt-8"
      >
        <h3 className="text-lg font-semibold text-text-primary mb-4">
          Quick Actions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => window.location.href = '/admin/control-center?tab=autoposter'}
            className="glass-card p-6 text-left hover:bg-glass-bg/50 transition-colors group"
          >
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-10 h-10 bg-accent-primary/20 rounded-lg flex items-center justify-center group-hover:bg-accent-primary/30 transition-colors">
                <BookOpen className="w-5 h-5 text-accent-primary" />
              </div>
              <h4 className="font-medium text-text-primary">Add Monument</h4>
            </div>
            <p className="text-sm text-text-secondary">
              Use Wikipedia Auto-Poster to quickly add new historical monuments
            </p>
          </button>

          <button
            onClick={() => window.location.href = '/admin/control-center?tab=users'}
            className="glass-card p-6 text-left hover:bg-glass-bg/50 transition-colors group"
          >
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-10 h-10 bg-accent-primary/20 rounded-lg flex items-center justify-center group-hover:bg-accent-primary/30 transition-colors">
                <Users className="w-5 h-5 text-accent-primary" />
              </div>
              <h4 className="font-medium text-text-primary">Manage Users</h4>
            </div>
            <p className="text-sm text-text-secondary">
              View and manage user accounts, roles, and ban status
            </p>
          </button>

          <button
            onClick={() => window.location.href = '/admin/control-center?tab=comments'}
            className="glass-card p-6 text-left hover:bg-glass-bg/50 transition-colors group"
          >
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-10 h-10 bg-accent-primary/20 rounded-lg flex items-center justify-center group-hover:bg-accent-primary/30 transition-colors">
                <MessageSquare className="w-5 h-5 text-accent-primary" />
              </div>
              <h4 className="font-medium text-text-primary">Moderate Comments</h4>
            </div>
            <p className="text-sm text-text-secondary">
              Review and moderate user comments across all monuments
            </p>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard;
